import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@material-ui/core";

const cx = (...p) => p.filter(Boolean).join(" ");

const BASE_WRAP = "grid grid-flow-col auto-cols-fr";
const BASE_BTN =
  "w-full text-sm font-bold text-center flex items-center justify-center transition active:scale-95";

const colorBase = {
  "gray50/white": "bg-gray50 border border-gray100 p-1",
  transparent: "",
  "transparent/gray50": "bg-transparent",
  "transparent/primary50": "bg-transparent",
  "primary700/primary50": "bg-primary50 border border-primary100 p-1",
};

const colorInside = {
  "gray50/white":
    "bg-transparent text-gray500 aria-selected:bg-white aria-selected:text-gray700 " +
    "aria-selected:shadow-[0px_1px_3px_0px_rgba(16,24,40,0.10),_0px_1px_2px_0px_rgba(16,24,40,0.06)]",
  transparent: "",
  "transparent/gray50":
    "bg-transparent text-gray500 aria-selected:bg-gray50 aria-selected:text-gray700",
  "transparent/primary50":
    "bg-transparent text-gray500 aria-selected:bg-primary50 aria-selected:text-primary700",
  "primary700/primary50":
    "bg-transparent text-primary700 aria-selected:bg-primary700 aria-selected:text-white",
};

const sizeInside = { large: "h-12", medium: "h-[2.375rem]", small: "h-8" };
const variantBase = { rounded: "rounded-full gap-2", underlined: "" };
const variantInside = { rounded: "rounded-full px-3", underlined: "px-2" };
const borderColorInside = {
  primary700:
    "border-b aria-selected:border-b-2 border-gray200 aria-selected:border-primary700",
  none: "",
};

function RawButton({
  isActive,
  isDisabled,
  onClick,
  children,
  size,
  variant,
  color,
  borderColor,
}) {
  const content = (
    <button
      aria-selected={isActive}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      onClick={(e) => {
        if (isDisabled) return;
        onClick?.(e);
      }}
      className={cx(
        BASE_BTN,
        sizeInside[size],
        variantInside[variant],
        colorInside[color],
        borderColorInside[borderColor],
        isDisabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
      )}
    >
      {children}
    </button>
  );
  return content;
}

export default function Tabs({
  activeTab = "",
  array = [],
  className = "",
  color = "transparent/primary50",
  size = "large",
  variant = "rounded",
  borderColor = "none",
  onTabChange,
}) {
  return (
    <div
      className={cx(
        BASE_WRAP,
        variantBase[variant],
        colorBase[color],
        className
      )}
    >
      {array.map(({ key, title, isDisabled, tooltip }) => {
        const isActive = key === activeTab;
        const btn = (
          <RawButton
            key={key}
            isActive={isActive}
            isDisabled={isDisabled}
            size={size}
            variant={variant}
            color={color}
            borderColor={borderColor}
            onClick={() => onTabChange?.(key)}
          >
            {title}
          </RawButton>
        );
        return isDisabled && tooltip ? (
          <Tooltip key={key} title={tooltip}>
            <span>{btn}</span>
          </Tooltip>
        ) : (
          btn
        );
      })}
    </div>
  );
}

Tabs.propTypes = {
  activeTab: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  array: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.node.isRequired,
      isDisabled: PropTypes.bool,
      tooltip: PropTypes.node,
    })
  ).isRequired,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    "gray50/white",
    "transparent",
    "transparent/gray50",
    "transparent/primary50",
    "primary700/primary50",
  ]),
  size: PropTypes.oneOf(["large", "medium"]),
  variant: PropTypes.oneOf(["rounded", "underlined"]),
  borderColor: PropTypes.oneOf(["none", "primary700"]),
  onTabChange: PropTypes.func.isRequired,
};
